import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, where, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import MessageBox from './MessageBox';
import NotificationBell from './NotificationBell'; // ✅ Added import

const HistoryPage = () => {
  const navigate = useNavigate();
  const [claimedItems, setClaimedItems] = useState([]);
  const [collectedItems, setCollectedItems] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('claimed');
  const [username, setUsername] = useState('');

  const showMessageBox = useCallback((msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
  }, []);

  const closeMessageBox = useCallback(() => {
    setMessage('');
    setMessageType('info');
  }, []);

  const checkAdminStatus = useCallback(async (user) => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setIsAdmin(userData.role === 'admin');
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  }, []);

  const fetchClaimedItems = useCallback(async () => {
    try {
      const claimedQuery = query(
        collection(db, 'items'),
        where('status', '==', 'claimed'),
        orderBy('claimedAt', 'desc'),
        limit(50)
      );
      const claimedSnapshot = await getDocs(claimedQuery);
      const claimedData = claimedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClaimedItems(claimedData);
    } catch (error) {
      console.error('Error loading claimed items:', error);
      showMessageBox('Error loading claimed items.', 'danger');
    }
  }, [showMessageBox]);

  const fetchCollectedItems = useCallback(async () => {
    try {
      const collectedQuery = query(
        collection(db, 'items'),
        where('status', '==', 'collected'),
        orderBy('collectedAt', 'desc'),
        limit(50)
      );
      const collectedSnapshot = await getDocs(collectedQuery);
      const collectedData = collectedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCollectedItems(collectedData);
    } catch (error) {
      console.error('Error loading collected items:', error);
      showMessageBox('Error loading collected items.', 'danger');
    }
  }, [showMessageBox]);

  const fetchAdminLogs = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const logsQuery = query(
        collection(db, 'admin_logs'),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      const logsSnapshot = await getDocs(logsQuery);
      const logsData = logsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAdminLogs(logsData);
    } catch (error) {
      console.error('Error loading admin logs:', error);
      showMessageBox('Error loading admin logs.', 'danger');
    }
  }, [isAdmin, showMessageBox]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const storedUsername = localStorage.getItem("username");
        setUsername(storedUsername || user.displayName || user.email);
        await checkAdminStatus(user);
        await fetchClaimedItems();
        await fetchCollectedItems();
      } else {
        localStorage.removeItem("username");
        navigate("/login");
      }
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, [navigate, checkAdminStatus, fetchClaimedItems, fetchCollectedItems]);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminLogs();
    }
  }, [isAdmin, fetchAdminLogs]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const now = new Date();
      const date = new Date(dateString);
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return `${Math.floor(diffDays / 30)} months ago`;
    } catch {
      return 'Unknown';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white animate-pulse select-none">
        {/* Navbar Skeleton */}
        <nav className="flex items-center justify-between h-16 px-6 border-b border-gray-300 bg-gray-100">
          <div className="bg-gray-300 w-36 h-8 rounded-md" />
          <div className="flex space-x-6 items-center">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-300 w-20 h-6 rounded-md" />
            ))}
            <div className="bg-gray-300 w-28 h-10 rounded-2xl" />
          </div>
        </nav>

        {/* Page Header & Tabs Skeleton */}
        <header className="max-w-7xl mx-auto my-8 px-4 space-y-6">
          <div className="bg-gray-300 w-48 h-8 rounded-md" />
          <div className="bg-gray-300 w-96 h-4 rounded-full" />
          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-300 mt-6">
            {[...Array(isAdmin ? 3 : 2)].map((_, i) => (
              <div key={i} className="bg-gray-300 h-10 rounded-t-lg w-36" />
            ))}
          </div>
        </header>

        {/* Tab Content Skeleton */}
        <main className="max-w-7xl mx-auto px-4 space-y-10">
          {/* Cards Grid for items or logs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-gray-100 rounded-xl p-4 space-y-4 flex flex-col animate-pulse h-full">
                <div className="bg-gray-300 rounded-t-xl h-48 w-full" />
                <div className="flex flex-col flex-1 space-y-3 p-3">
                  <div className="bg-gray-300 h-6 w-3/4 rounded-md" />
                  <div className="bg-gray-300 h-4 w-full rounded-full" />
                  <div className="bg-gray-300 h-4 w-full rounded-full" />
                  <div className="bg-gray-300 h-4 w-1/2 rounded-full" />
                  <div className="bg-gray-300 h-10 w-full rounded-lg mt-auto" />
                </div>
              </div>
            ))}
          </div>

          {/* For Admin Logs (table) - Simple skeleton rows */}
          {isAdmin && (
            <section>
              <div className="overflow-x-auto rounded-xl border bg-white shadow">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-yellow-100">
                    <tr>
                      {['Action', 'Item ID', 'Admin', 'Timestamp', 'Details'].map((header, i) => (
                        <th key={i} className="p-3 border-b border-yellow-300">
                          <div className="bg-gray-300 h-6 w-24 rounded-md mx-auto" />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(5)].map((_, i) => (
                      <tr key={i} className="border-b border-yellow-50 even:bg-yellow-50">
                        {[...Array(5)].map((__, j) => (
                          <td key={j} className="p-3">
                            <div className="bg-gray-300 h-5 w-20 rounded-md mx-auto" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </main>

        {/* Footer Skeleton */}
        <footer className="bg-[#000034] text-white pt-12 pb-5 mt-20 border-t border-blue-900 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-4">
                <div className="bg-gray-300 h-6 w-40 rounded-md" />
                <ul className="space-y-2">
                  {[...Array(4)].map((__, idx) => (
                    <li key={idx}>
                      <div className="bg-gray-300 h-4 w-full rounded-md" />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <hr className="my-5 border-blue-700 opacity-40" />
          <div className="text-center">
            <div className="bg-gray-300 h-4 w-48 mx-auto rounded-md" />
            <div className="bg-gray-300 h-3 w-72 mx-auto rounded-full mt-2" />
          </div>
        </footer>
      </div>
    );
  }

  return (
    <>
      {/* Navbar */}
      <nav className="flex items-center bg-white border-b px-6 py-3 shadow sticky top-0 z-40">
        <Link className="my-nav-link mr-2" to="/homepage">
          <img 
            src="/Christ_logo_latest.jpg" 
            alt="Christ University Logo" 
            className="logo-christ max-w-[150px] h-auto"
          />
        </Link>
        <div className="my-nav-link ml-auto flex items-center gap-3">
          {/* ✅ Added NotificationBell component here */}
          <NotificationBell />
          <span className="font-bold text-blue-900">
            Welcome: {username}
            {isAdmin && (
              <span className="ml-2 px-2 py-1 rounded-lg bg-yellow-200 text-yellow-900 text-xs font-semibold">
                Admin
              </span>
            )}
          </span>
          <ul className="flex gap-2 items-center ml-6 text-sm font-semibold">
            <li>
              <Link className="my-nav-link px-2 py-1 text-blue-900 hover:underline" to="/homepage">
                HOME
              </Link>
            </li>
            {isAdmin && (
              <li>
                <Link className="my-nav-link px-2 py-1 text-yellow-700 hover:underline" to="/admin/dashboard">
                  ADMIN PANEL
                </Link>
              </li>
            )}
            <li>
              <Link className="my-nav-link px-2 py-1 text-blue-900 hover:underline" to="/dashboard">
                DASHBOARD
              </Link>
            </li>
            <li>
              <button
                onClick={handleLogout}
                type="button"
                className="bg-white text-center w-48 rounded-2xl h-14 relative text-black text-xl font-semibold group"
              >
                <div className="bg-red-400 rounded-xl h-12 w-1/4 flex items-center justify-center absolute left-1 top-[4px] group-hover:w-[184px] z-10 duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" height="25px" width="25px">
                    <path d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z" fill="#000000"></path>
                    <path d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z" fill="#000000"></path>
                  </svg>
                </div>
                <p className="translate-x-2">LOGOUT</p>
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto my-8 px-4">
        {message && <MessageBox message={message} type={messageType} onClose={closeMessageBox} />}

        {/* Page Header */}
        <div className="text-center mb-8">
          <h2 className="font-bold text-blue-900 text-2xl mb-2">
            Claims History
          </h2>
          <p className="text-gray-500">Track recently claimed and collected lost & found items</p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            className={`py-2 px-5 rounded-t-lg font-semibold text-base transition-all ${
              activeTab === 'claimed'
                ? 'text-green-700 bg-green-100 border-b-2 border-green-700'
                : 'text-gray-500 hover:text-green-700'
            }`}
            onClick={() => setActiveTab('claimed')}
          >
            Claimed Items ({claimedItems.length})
          </button>
          <button
            className={`py-2 px-5 rounded-t-lg font-semibold text-base transition-all ${
              activeTab === 'collected'
                ? 'text-blue-600 bg-blue-100 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-blue-700'
            }`}
            onClick={() => setActiveTab('collected')}
          >
            Collected Items ({collectedItems.length})
          </button>
          {isAdmin && (
            <button
              className={`py-2 px-5 rounded-t-lg font-semibold text-base transition-all ${
                activeTab === 'logs'
                  ? 'text-yellow-700 bg-yellow-100 border-b-2 border-yellow-500'
                  : 'text-gray-600 hover:text-yellow-700'
              }`}
              onClick={() => setActiveTab('logs')}
            >
              Admin Logs ({adminLogs.length})
            </button>
          )}
        </div>

        {/* Claimed Items Tab */}
        {activeTab === 'claimed' && (
          <div>
            <h4 className="mb-5 text-green-700 font-bold text-xl">
              Recently Claimed Items
            </h4>
            {claimedItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
                {claimedItems.map((item) => (
                  <div key={item.id} className="bg-white h-full rounded-xl shadow border border-green-200 flex flex-col">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        className="w-full h-48 object-cover rounded-t-xl"
                        alt={item.itemName || 'Item'}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="p-4 flex-1 flex flex-col">
                      <h5 className="font-semibold text-green-800 text-lg mb-1">
                        {item.itemName || item.foundItemName || 'Unknown Item'}
                      </h5>
                      <p className="text-gray-500 text-sm mb-2">{item.description}</p>
                      <div className="my-2 flex gap-2 flex-wrap">
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                          Claimed
                        </span>
                        <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                          {item.category}
                        </span>
                      </div>
                      <div className="mt-auto text-xs text-gray-500">
                        <div>
                          <span className="font-medium">Claimed:</span> {getTimeSince(item.claimedAt)}
                        </div>
                        <div>
                          <span className="font-medium">Date:</span> {formatDate(item.claimedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t border-green-100">
                      <Link 
                        to={`/itemdetails/${item.id}`}
                        className="my-nav-link relative w-fit no-underline flex items-center px-5 py-2 overflow-hidden font-medium transition-all bg-orange-500 rounded-md group mt-auto"
                      >
                        <span className="absolute top-0 right-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-orange-800 rounded group-hover:-mr-4 group-hover:-mt-4">
                          <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
                        </span>
                        <span className="absolute bottom-0 rotate-180 left-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-orange-800 rounded group-hover:-ml-4 group-hover:-mb-4">
                          <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
                        </span>
                        <span className="absolute bottom-0 left-0 w-full h-full transition-all duration-500 ease-in-out delay-200 -translate-x-full bg-orange-700 rounded-md group-hover:translate-x-0"></span>
                        <span className="relative text-white transition-colors duration-200 ease-in-out group-hover:text-white">
                          View Details
                        </span>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p className="text-lg italic">No claimed items found in recent history.</p>
              </div>
            )}
          </div>
        )}

        {/* Collected Items Tab */}
        {activeTab === 'collected' && (
          <div>
            <h4 className="mb-5 text-blue-700 font-bold text-xl">
              Recently Collected Items
            </h4>
            {collectedItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
                {collectedItems.map((item) => (
                  <div key={item.id} className="bg-white h-full rounded-xl shadow border border-blue-200 flex flex-col">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        className="w-full h-48 object-cover rounded-t-xl"
                        alt={item.itemName || 'Item'}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="p-4 flex-1 flex flex-col">
                      <h5 className="font-semibold text-blue-800 text-lg mb-1">
                        {item.itemName || item.foundItemName || 'Unknown Item'}
                      </h5>
                      <p className="text-gray-500 text-sm mb-2">{item.description}</p>
                      <div className="my-2 flex gap-2 flex-wrap">
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                          Collected
                        </span>
                        <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                          {item.category}
                        </span>
                      </div>
                      <div className="mt-auto text-xs text-gray-500">
                        <div>
                          <span className="font-medium">Collected:</span> {getTimeSince(item.collectedAt)}
                        </div>
                        <div>
                          <span className="font-medium">Date:</span> {formatDate(item.collectedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t border-blue-100">
                      <Link 
                        to={`/itemdetails/${item.id}`}
                        className="my-nav-link relative w-fit no-underline flex items-center px-5 py-2 overflow-hidden font-medium transition-all bg-green-600 rounded-md group mt-auto"
                      >
                        <span className="absolute top-0 right-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-green-800 rounded group-hover:-mr-4 group-hover:-mt-4">
                          <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
                        </span>
                        <span className="absolute bottom-0 rotate-180 left-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-green-800 rounded group-hover:-ml-4 group-hover:-mb-4">
                          <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
                        </span>
                        <span className="absolute bottom-0 left-0 w-full h-full transition-all duration-500 ease-in-out delay-200 -translate-x-full bg-green-700 rounded-md group-hover:translate-x-0"></span>
                        <span className="relative text-white transition-colors duration-200 ease-in-out group-hover:text-white">
                          View Details
                        </span>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p className="text-lg italic">No collected items found in recent history.</p>
              </div>
            )}
          </div>
        )}

        {/* Admin Logs Tab */}
        {activeTab === 'logs' && isAdmin && (
          <div>
            <h4 className="mb-5 text-yellow-700 font-bold text-xl">
              Admin Activity Logs
            </h4>
            {adminLogs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border text-sm shadow rounded-xl overflow-hidden bg-white">
                  <thead>
                    <tr className="bg-yellow-100 text-yellow-900 font-semibold">
                      <th className="p-3 text-left">Action</th>
                      <th className="p-3 text-left">Item ID</th>
                      <th className="p-3 text-left">Admin</th>
                      <th className="p-3 text-left">Timestamp</th>
                      <th className="p-3 text-left">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminLogs.map((log) => (
                      <tr key={log.id} className="even:bg-yellow-50 hover:bg-yellow-100 transition-colors">
                        <td className="p-3">
                          <span className="inline-block px-2 py-1 rounded bg-yellow-200 text-yellow-900 font-semibold text-xs">
                            {log.action?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
                          </span>
                        </td>
                        <td className="p-3">
                          <code className="text-xs bg-gray-100 p-1 rounded">
                            {log.itemId ? log.itemId.substring(0, 8) + '...' : 'N/A'}
                          </code>
                        </td>
                        <td className="p-3 text-xs">
                          {log.adminId ? log.adminId.substring(0, 8) + '...' : 'System'}
                        </td>
                        <td className="p-3 text-xs">{formatDate(log.timestamp)}</td>
                        <td className="p-3 text-gray-500 text-xs max-w-xs truncate">
                          {log.reason || log.description || 'No details'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p className="text-lg italic">No admin activity logs found.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#000034] text-white pt-12 pb-5 mt-20 border-t border-blue-900 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h6 className="font-bold mb-3">Quick Links</h6>
              <ul className="space-y-1 text-white/70">
                <li><Link to="/homepage" className="hover:underline">Home</Link></li>
                <li><Link to="/reportitem" className="hover:underline">Report Item</Link></li>
                <li><Link to="/history" className="hover:underline">History</Link></li>
                <li><Link to="/dashboard" className="hover:underline">Dashboard</Link></li>
              </ul>
            </div>
            <div>
              <h6 className="font-bold mb-3">Services</h6>
              <ul className="space-y-1 text-white/70">
                <li><Link to="/all-lost-items" className="hover:underline">Lost Items</Link></li>
                <li><Link to="/all-found-items" className="hover:underline">Found Items</Link></li>
                <li><span>Claims Tracking</span></li>
                <li><span>Office Collection</span></li>
              </ul>
            </div>
            <div>
              <h6 className="font-bold mb-3">Contact</h6>
              <ul className="space-y-1 text-white/70">
                <li>Christ University, Bangalore</li>
                <li>info@christuniversity.in</li>
                <li>+91 12345 67890</li>
              </ul>
            </div>
          </div>
          <hr className="my-5 border-blue-900 opacity-40" />
          <div className="text-center">
            <small className="text-white/50">
              © 2024 Lost and Found Christ University. All rights reserved.
            </small>
          </div>
        </div>
      </footer>
    </>
  );
};

export default HistoryPage;
